import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { Trash2, RotateCcw, Search, Filter, Clock, User, Info, FileText, AlertTriangle, Server, Briefcase, Eye, X } from 'lucide-react'
import { ConfirmModal } from '../components/ConfirmModal'

function DetailModal({ item, onClose }) {
  if (!item) return null
  const content = JSON.parse(item.content)
  
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {item.item_type === 'risk' && <AlertTriangle size={20} color="var(--risk-high)" />}
              {item.item_type === 'asset' && <Server size={20} color="var(--accent)" />}
              {item.item_type === 'document' && <FileText size={20} color="var(--info)" />}
              {item.item_type === 'process' && <Briefcase size={20} color="var(--warning)" />}
            </div>
            <div>
              <h2 style={{ fontSize: '1.25rem' }}>{item.item_name}</h2>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Deleted from {item.item_type}s</p>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--bg-base)', padding: 16, borderRadius: 12, marginBottom: 20, border: '1px solid var(--border)' }}>
             <h4 style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12, letterSpacing: '0.05em' }}>Deletion Metadata</h4>
             <div className="grid-2">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Clock size={14} color="var(--text-muted)" />
                  <span style={{ fontSize: '0.875rem' }}>{new Date(item.deleted_at).toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <User size={14} color="var(--text-muted)" />
                  <span style={{ fontSize: '0.875rem' }}>Deleted by {item.deleted_by}</span>
                </div>
             </div>
             <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <p style={{ fontSize: '0.875rem', fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                  " {item.reason || 'No reason provided'} "
                </p>
             </div>
          </div>

          <h4 style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12, letterSpacing: '0.05em' }}>Record Content</h4>
          <pre style={{ 
            background: 'var(--bg-base)', 
            padding: 16, 
            borderRadius: 8, 
            fontSize: '0.8125rem', 
            overflow: 'auto',
            maxHeight: 300,
            color: 'var(--text-secondary)',
            border: '1px solid var(--border)'
          }}>
            {JSON.stringify(content, null, 2)}
          </pre>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

export function Bin({ toast, user }) {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [viewing, setViewing] = useState(null)
  const [confirmPurge, setConfirmPurge] = useState(null)

  const isAdmin = user?.role === 'Admin'

  const { data: items = [], isLoading } = useQuery({ queryKey: ['recycle-bin'], queryFn: api.getRecycleBin })

  const restoreMut = useMutation({
    mutationFn: api.restoreFromBin,
    onSuccess: () => { qc.invalidateQueries(); toast('Item restored successfully', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const purgeMut = useMutation({
    mutationFn: api.purgeFromBin,
    onSuccess: () => { qc.invalidateQueries(['recycle-bin']); toast('Item permanently deleted', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const filtered = items.filter(i => 
    i.item_name.toLowerCase().includes(search.toLowerCase()) || 
    i.item_type.toLowerCase().includes(search.toLowerCase()) ||
    (i.reason && i.reason.toLowerCase().includes(search.toLowerCase()))
  )

  const typeColor = (t) => ({
    'risk': 'badge-warning',
    'asset': 'badge-info',
    'document': 'badge-success',
    'process': 'badge-neutral'
  }[t] || 'badge-neutral')

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Recycle Bin</h1>
          <p>Restore or permanently delete removed records</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input 
            className="form-input" 
            style={{ paddingLeft: 40 }} 
            placeholder="Search by name, type, or reason..." 
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Item Name</th>
              <th>Type</th>
              <th>Reason for Deletion</th>
              <th>Deleted At</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5}><div className="empty-state">Loading...</div></td></tr>}
            {!isLoading && filtered.length === 0 && (
              <tr><td colSpan={5}><div className="empty-state" style={{ padding: 60 }}>
                <Trash2 size={48} color="var(--border)" />
                <p>Recycle Bin is empty.</p>
              </div></td></tr>
            )}
            {filtered.map(item => (
              <tr key={item.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {item.item_type === 'risk' && <AlertTriangle size={14} color="var(--risk-high)" />}
                    {item.item_type === 'asset' && <Server size={14} color="var(--accent)" />}
                    {item.item_type === 'document' && <FileText size={14} color="var(--info)" />}
                    <span style={{ fontWeight: 600 }}>{item.item_name}</span>
                  </div>
                </td>
                <td><span className={`badge ${typeColor(item.item_type)}`}>{item.item_type}</span></td>
                <td>
                  <div style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8125rem', fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                    {item.reason || '—'}
                  </div>
                </td>
                <td style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{new Date(item.deleted_at).toLocaleString()}</td>
                <td>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setViewing(item)} title="View Details">
                      <Eye size={14} />
                    </button>
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent)' }} onClick={() => restoreMut.mutate(item.id)} title="Restore">
                      <RotateCcw size={14} />
                    </button>
                    {isAdmin && (
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => setConfirmPurge(item)} title="Permanent Delete">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DetailModal item={viewing} onClose={() => setViewing(null)} />
      <ConfirmModal 
        isOpen={!!confirmPurge}
        onClose={() => setConfirmPurge(null)}
        onConfirm={() => purgeMut.mutate(confirmPurge.id)}
        title="Permanently Delete?"
        message={`Delete "${confirmPurge?.item_name}" forever? This action cannot be undone.`}
        showReason={false}
      />
    </div>
  )
}
